import React, { useEffect, useState } from 'react';
import { ArrowLeft, Upload, Calendar, MapPin, User, FileText, Building, Search, Archive, Download } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { companyInfo } from '../mockData';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';

const PlaceOrderPage = () => {
  const [searchParams] = useSearchParams();
  const [approvedQuote, setApprovedQuote] = useState(null);
  const [formStartedAt] = useState(() => Date.now());
  const [orderData, setOrderData] = useState({
    orderType: '',
    urgency: 'routine',
    // Common fields
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientAddress: '',
    
    // Required common case fields
    caseNumberCommon: '',
    courtAndBranch: '',
    plaintiffName: '',
    plaintiffAddress: '',
    defendantName: '',
    defendantAddress: '',
    
    // Process Serving fields
    documentType: '',
    serviceAddress: '',
    recipientName: '',
    recipientDescription: '',
    specialInstructions: '',
    
    // Court Filing fields
    courtName: '',
    caseNumber: '',
    documentsToFile: '',
    filingDeadline: '',
    copiesNeeded: '1',
    
    // Recording fields
    county: '',
    recordingDocumentType: '',
    partyNames: '',
    
    // Document Retrieval fields
    retrievalCourt: '',
    retrievalCaseNumber: '',
    retrievalDocumentType: '',
    dateRange: '',
    
    // Skip Tracing fields
    subjectName: '',
    subjectLastKnown: '',
    subjectDOB: '',
    subjectSSN: '',
    relationshipToSubject: '',
    reasonForSearch: ''
  });

  const [files, setFiles] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedOrderId, setSubmittedOrderId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');



  useEffect(() => {
    const token = searchParams.get('approve');
    if (!token) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quote-approval/${token}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((q) => {
        if (!q) return;
        setApprovedQuote(q);
        const urgencyMap = { routine: 'routine', standard: 'routine', priority: 'priority', rush: 'urgent', urgent: 'urgent', 'on-demand': 'on-demand', 'after-hours': 'urgent' };
        const serviceMap = { 'process-serving': 'process-serving', 'court-filing': 'court-filing', 'e-filing': 'e-filing', 'document-retrieval': 'document-retrieval', 'skip-tracing': 'skip-tracing', 'county-recording': 'recording' };
        setOrderData((prev) => ({
          ...prev,
          orderType: serviceMap[q.service] || prev.orderType,
          clientName: q.name || '', clientEmail: q.email || '', clientPhone: q.phone || '',
          courtAndBranch: q.court || '', caseNumberCommon: q.case_number || '',
          courtName: q.court || '', caseNumber: q.case_number || '',
          urgency: urgencyMap[(q.urgency || '').toLowerCase().split(' ')[0]] || prev.urgency,
          specialInstructions: `Approving quote #${q.reference} for $${Number(q.amount).toFixed(2)}.`
        }));
      })
      .catch(() => {});
  }, [searchParams]);

  const orderTypes = [
    { value: 'process-serving', label: 'Process Serving', icon: FileText },
    { value: 'court-filing', label: 'Court Filing (in person)', icon: Building },
    { value: 'e-filing', label: 'E-Filing', icon: Upload },
    { value: 'recording', label: 'County Recording', icon: Archive },
    { value: 'document-retrieval', label: 'Document Retrieval', icon: Download },
    { value: 'skip-tracing', label: 'Skip Tracing', icon: Search }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrderData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // Use FormData to send files along with order data
      const formData = new FormData();
      formData.append('website', '');
      formData.append('form_started_at', String(formStartedAt));
      if (approvedQuote) formData.append('approved_quote_ref', approvedQuote.reference);
      
      // Add all order fields to FormData
      formData.append('order_type', orderData.orderType);
      formData.append('urgency', orderData.urgency);
      formData.append('client_name', orderData.clientName);
      formData.append('client_email', orderData.clientEmail);
      formData.append('client_phone', orderData.clientPhone || '');
      formData.append('client_address', orderData.clientAddress || '');
      formData.append('case_number_common', orderData.caseNumberCommon || '');
      formData.append('court_and_branch', orderData.courtAndBranch);
      formData.append('plaintiff_name', orderData.plaintiffName);
      formData.append('plaintiff_address', orderData.plaintiffAddress);
      formData.append('defendant_name', orderData.defendantName);
      formData.append('defendant_address', orderData.defendantAddress);
      formData.append('document_type', orderData.documentType || '');
      formData.append('service_address', orderData.serviceAddress || '');
      formData.append('recipient_name', orderData.recipientName || '');
      formData.append('recipient_description', orderData.recipientDescription || '');
      formData.append('court_name', orderData.courtName || '');
      formData.append('documents_to_file', orderData.documentsToFile || '');
      formData.append('filing_deadline', orderData.filingDeadline || '');
      formData.append('copies_needed', orderData.copiesNeeded || '');
      formData.append('county', orderData.county || '');
      formData.append('recording_document_type', orderData.recordingDocumentType || '');
      formData.append('party_names', orderData.partyNames || '');
      formData.append('retrieval_court', orderData.retrievalCourt || '');
      formData.append('retrieval_case_number', orderData.retrievalCaseNumber || '');
      formData.append('retrieval_document_type', orderData.retrievalDocumentType || '');
      formData.append('date_range', orderData.dateRange || '');
      formData.append('subject_name', orderData.subjectName || '');
      formData.append('subject_last_known', orderData.subjectLastKnown || '');
      formData.append('subject_dob', orderData.subjectDOB || '');
      formData.append('relationship_to_subject', orderData.relationshipToSubject || '');
      formData.append('reason_for_search', orderData.reasonForSearch || '');
      formData.append('special_instructions', orderData.specialInstructions || '');
      
      // Add uploaded files to FormData
      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders-with-files`, {
        method: 'POST',
        body: formData, // Send FormData instead of JSON
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        let errorMessage = 'Failed to submit order';
        
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map(err => {
              if (typeof err === 'string') return err;
              if (err.msg) return err.msg;
              if (err.message) return err.message;
              return JSON.stringify(err);
            }).join(', ');
          } else if (typeof errorData.detail === 'object') {
            errorMessage = errorData.detail.message || errorData.detail.msg || JSON.stringify(errorData.detail);
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      // Set success state
      setSubmittedOrderId(result.order_id || null);
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Reset form after successful submission (8 seconds)
      setTimeout(() => {
        setIsSubmitted(false);
        setOrderData({
          orderType: '',
          urgency: 'routine',
          clientName: '',
          clientEmail: '',
          clientPhone: '',
          clientAddress: '',
          caseNumberCommon: '',
          courtAndBranch: '',
          plaintiffName: '',
          plaintiffAddress: '',
          defendantName: '',
          defendantAddress: '',
          documentType: '',
          serviceAddress: '',
          recipientName: '',
          recipientDescription: '',
          specialInstructions: '',
          courtName: '',
          caseNumber: '',
          documentsToFile: '',
          filingDeadline: '',
          copiesNeeded: '1',
          county: '',
          recordingDocumentType: '',
          partyNames: '',
          retrievalCourt: '',
          retrievalCaseNumber: '',
          retrievalDocumentType: '',
          dateRange: '',
          subjectName: '',
          subjectLastKnown: '',
          subjectDOB: '',
          subjectSSN: '',
          relationshipToSubject: '',
          reasonForSearch: ''
        });
        setFiles([]);
      }, 8000);
      
    } catch (err) {
      
      let displayError = 'Failed to submit order. Please try again.';
      
      if (err.message) {
        displayError = err.message;
      } else if (typeof err === 'string') {
        displayError = err;
      } else if (err.detail) {
        displayError = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail);
      } else {
        displayError = `Submission error: ${JSON.stringify(err)}`;
      }
      
      setError(displayError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderOrderTypeFields = () => {
    switch (orderData.orderType) {
      case 'process-serving':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h3 className="heading-3" style={{ fontSize: '1.3rem', marginBottom: '1rem' }}>
              Process Serving Details
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  Document Type *
                </label>
                <select
                  name="documentType"
                  value={orderData.documentType}
                  onChange={handleInputChange}
                  required={true}
                  style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                >
                  <option value="">Select document type...</option>
                  <option value="summons">Summons & Complaint</option>
                  <option value="subpoena">Subpoena</option>
                  <option value="notice">Notice to Quit</option>
                  <option value="court-order">Court Order</option>
                  <option value="divorce">Divorce Papers</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  Recipient Name *
                </label>
                <input
                  type="text"
                  name="recipientName"
                  value={orderData.recipientName}
                  onChange={handleInputChange}
                  required={true}
                  placeholder="Full name of person to be served"
                  style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                />
              </div>
            </div>

            <div>
              <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                Service Address *
              </label>
              <textarea
                name="serviceAddress"
                value={orderData.serviceAddress}
                onChange={handleInputChange}
                required={true}
                rows="3"
                placeholder="Complete address where service should be attempted"
                style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem', resize: 'vertical' }}
              />
            </div>

            <div>
              <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                Recipient Description
              </label>
              <input
                type="text"
                name="recipientDescription"
                value={orderData.recipientDescription}
                onChange={handleInputChange}
                placeholder="Physical description, age, etc."
                style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
              />
            </div>
          </div>
        );

      case 'court-filing':
      case 'e-filing':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h3 className="heading-3" style={{ fontSize: '1.3rem', marginBottom: '1rem' }}>
              {orderData.orderType === 'e-filing' ? 'E-Filing Details' : 'Court Filing Details'}
            </h3>
            
            <div>
              <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                Documents to file *
              </label>
              <textarea
                name="documentsToFile"
                data-testid="order-documents-to-file"
                value={orderData.documentsToFile}
                onChange={handleInputChange}
                required
                rows={3}
                placeholder="List the document titles, e.g. Complaint; Civil Case Cover Sheet; Summons"
                style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem', fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  Filing Deadline
                </label>
                <input
                  type="date"
                  name="filingDeadline"
                  value={orderData.filingDeadline}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                />
              </div>
              
              {orderData.orderType !== 'e-filing' && (
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  Copies Needed
                </label>
                <select
                  name="copiesNeeded"
                  value={orderData.copiesNeeded}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                >
                  <option value="1">1 Copy</option>
                  <option value="2">2 Copies</option>
                  <option value="3">3 Copies</option>
                  <option value="4">4 Copies</option>
                  <option value="5">5+ Copies</option>
                </select>
              </div>
              )}
            </div>
          </div>
        );

      case 'recording':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h3 className="heading-3" style={{ fontSize: '1.3rem', marginBottom: '1rem' }}>
              County Recording Details
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  County *
                </label>
                <input
                  type="text"
                  name="county"
                  value={orderData.county}
                  onChange={handleInputChange}
                  required={true}
                  placeholder="e.g., Los Angeles County"
                  style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                />
              </div>
              
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  Document Type *
                </label>
                <select
                  name="recordingDocumentType"
                  value={orderData.recordingDocumentType}
                  onChange={handleInputChange}
                  required={true}
                  style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                >
                  <option value="">Select document type...</option>
                  <option value="deed">Deed</option>
                  <option value="lien">Lien</option>
                  <option value="lis-pendens">Lis Pendens</option>
                  <option value="notice">Notice</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                Party Names
              </label>
              <textarea
                name="partyNames"
                value={orderData.partyNames}
                onChange={handleInputChange}
                rows="2"
                placeholder="Names of all parties involved in the document"
                style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem', resize: 'vertical' }}
              />
            </div>
          </div>
        );

      case 'document-retrieval':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h3 className="heading-3" style={{ fontSize: '1.3rem', marginBottom: '1rem' }}>
              Document Retrieval Details
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  Court/Agency *
                </label>
                <input
                  type="text"
                  name="retrievalCourt"
                  value={orderData.retrievalCourt}
                  onChange={handleInputChange}
                  required={true}
                  placeholder="Court or agency where documents are located"
                  style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                />
              </div>
              
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  Case Number
                </label>
                <input
                  type="text"
                  name="retrievalCaseNumber"
                  value={orderData.retrievalCaseNumber}
                  onChange={handleInputChange}
                  placeholder="Case number if known"
                  style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  Document Type *
                </label>
                <input
                  type="text"
                  name="retrievalDocumentType"
                  value={orderData.retrievalDocumentType}
                  onChange={handleInputChange}
                  required={true}
                  placeholder="Type of documents needed"
                  style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                />
              </div>
              
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  Date Range
                </label>
                <input
                  type="text"
                  name="dateRange"
                  value={orderData.dateRange}
                  onChange={handleInputChange}
                  placeholder="e.g., January 2023 - Present"
                  style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                />
              </div>
            </div>
          </div>
        );

      case 'skip-tracing':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h3 className="heading-3" style={{ fontSize: '1.3rem', marginBottom: '1rem' }}>
              Skip Tracing Details
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  Subject Name *
                </label>
                <input
                  type="text"
                  name="subjectName"
                  value={orderData.subjectName}
                  onChange={handleInputChange}
                  required={true}
                  placeholder="Full name of person to locate"
                  style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                />
              </div>
              
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="subjectDOB"
                  value={orderData.subjectDOB}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                />
              </div>
            </div>

            <div>
              <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                Last Known Address/Information *
              </label>
              <textarea
                name="subjectLastKnown"
                value={orderData.subjectLastKnown}
                onChange={handleInputChange}
                required={true}
                rows="3"
                placeholder="Last known address, employer, phone number, email, etc."
                style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem', resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  Relationship to Subject
                </label>
                <select
                  name="relationshipToSubject"
                  value={orderData.relationshipToSubject}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                >
                  <option value="">Select relationship...</option>
                  <option value="attorney">Attorney/Legal Representative</option>
                  <option value="creditor">Creditor</option>
                  <option value="family">Family Member</option>
                  <option value="business">Business Associate</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  Reason for Search *
                </label>
                <select
                  name="reasonForSearch"
                  value={orderData.reasonForSearch}
                  onChange={handleInputChange}
                  required={true}
                  style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                >
                  <option value="">Select reason...</option>
                  <option value="legal-service">Legal Service</option>
                  <option value="debt-collection">Debt Collection</option>
                  <option value="asset-recovery">Asset Recovery</option>
                  <option value="witness-location">Witness Location</option>
                  <option value="other">Other Legal Purpose</option>
                </select>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (isSubmitted) {
    return (
      <div>
        <Header />
        <section style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', paddingTop: '160px', paddingBottom: '80px' }}>
          <div className="container">
            <div style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
              <div style={{ 
                backgroundColor: 'var(--success-green)', 
                width: '80px', 
                height: '80px', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                margin: '0 auto 2rem auto'
              }}>
                <FileText size={40} color="white" />
              </div>
              
              <h1 className="heading-1" style={{ marginBottom: '1rem', color: 'var(--success-green)' }}>
                Order Submitted Successfully!
              </h1>
              
              <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                Thank you for your order. We've received your request and will contact you within 24 hours to confirm details and provide pricing.
              </p>
              {submittedOrderId && (
                <p className="body-medium" data-testid="order-success-id" style={{ color: 'var(--text-primary)', fontWeight: '700', marginBottom: '2rem' }}>
                  Order ID: {submittedOrderId} · <Link to={`/status?id=${submittedOrderId}`} data-testid="order-success-status-link" style={{ color: 'var(--brand-primary)' }}>track status</Link>
                </p>
              )}
              
              <div style={{ 
                backgroundColor: 'var(--bg-card)', 
                padding: '2rem', 
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                marginBottom: '2rem'
              }}>
                <p className="body-medium" style={{ color: 'var(--text-primary)', marginBottom: '1rem' }}>
                  <strong>Next Steps:</strong>
                </p>
                <ul style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                  <li>We'll review your order details</li>
                  <li>Contact you to confirm requirements</li>
                  <li>Provide final pricing and timeline</li>
                  <li>Begin work upon approval</li>
                </ul>
              </div>
              
              <Link to="/" className="btn-primary">
                Return to Home
              </Link>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <SEOHelmet 
        title="Place Order | Legal Document Services California | Process Server"
        description="Place your order for process serving, court filing, or legal document services. Same-day, priority & routine service available across California. Registered & bonded."
        keywords="place order process server, order court filing, legal document services order, California process serving order, same day service"
        canonical="/place-order"
      />
      <Header />
      
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          {/* Back Button */}
          <Link 
            to="/" 
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'var(--brand-primary)',
              textDecoration: 'none',
              marginBottom: '2rem',
              fontSize: '1rem',
              fontWeight: '500'
            }}
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>

          {/* Page Header */}
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h1 className="heading-1" style={{ marginBottom: '1rem' }}>
              Place Your Order
            </h1>
            <p className="body-large" style={{ 
              color: 'var(--text-secondary)',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Submit your legal document service request. We'll review and contact you within 24 hours with pricing and timeline.
            </p>
          </div>

          <form onSubmit={handleSubmit} style={{ maxWidth: '900px', margin: '0 auto' }}>
            {/* Order Type Selection */}
            <div style={{ 
              backgroundColor: 'var(--bg-card)', 
              padding: '2rem', 
              borderRadius: '12px', 
              border: '1px solid var(--border-medium)',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ fontSize: '1.3rem', marginBottom: '1.5rem' }}>
                Service Type
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                {orderTypes.map((type) => {
                  const IconComponent = type.icon;
                  return (
                    <label 
                      key={type.value}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '1.5rem',
                        border: `2px solid ${orderData.orderType === type.value ? 'var(--brand-primary)' : 'var(--border-medium)'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        backgroundColor: orderData.orderType === type.value ? 'rgba(44, 90, 160, 0.05)' : 'transparent'
                      }}
                    >
                      <input
                        type="radio"
                        name="orderType"
                        data-testid={`order-type-${type.value}`}
                        value={type.value}
                        checked={orderData.orderType === type.value}
                        onChange={handleInputChange}
                        style={{ display: 'none' }}
                      />
                      <IconComponent 
                        size={32} 
                        color={orderData.orderType === type.value ? 'var(--brand-primary)' : 'var(--text-secondary)'} 
                        style={{ marginBottom: '0.5rem' }}
                      />
                      <span style={{ 
                        fontSize: '0.9rem', 
                        fontWeight: '600',
                        color: orderData.orderType === type.value ? 'var(--brand-primary)' : 'var(--text-primary)',
                        textAlign: 'center'
                      }}>
                        {type.label}
                      </span>
                    </label>
                  );
                })}
              </div>

              {/* Urgency */}
              <div>
                <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                  Service Level *
                </label>
                <select
                  name="urgency"
                  value={orderData.urgency}
                  onChange={handleInputChange}
                  required={true}
                  style={{ width: '100%', maxWidth: '300px', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                >
                  {(orderData.orderType === 'e-filing' ? [
                    ['routine', 'Standard (same or next business day)'], ['priority', 'Priority (within 4 court hours)'],
                    ['urgent', 'Rush (within 2 hours during court hours)'], ['on-demand', 'After-hours submission for next-day stamp']
                  ] : [
                    ['routine', 'Routine (4 days)'], ['priority', 'Priority (Next Business Day)'],
                    ['urgent', 'Urgent (Same Day)'], ['on-demand', 'On-Demand (< 2 hours)']
                  ]).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <p className="body-small" style={{ color: 'var(--text-secondary)', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  {orderData.orderType === 'e-filing' ? '*Submission times depend on the court\'s e-filing system availability; the court controls acceptance and file-stamp time.' : '*Timelines refer to service attempts. Successful service depends on recipient availability.'}
                </p>
              </div>
            </div>

            {/* Client Information */}
            <div style={{ 
              backgroundColor: 'var(--bg-card)', 
              padding: '2rem', 
              borderRadius: '12px', 
              border: '1px solid var(--border-medium)',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ fontSize: '1.3rem', marginBottom: '1.5rem' }}>
                Contact Information
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="clientName"
                    value={orderData.clientName}
                    onChange={handleInputChange}
                    required={true}
                    style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                  />
                </div>
                
                <div>
                  <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    name="clientEmail"
                    value={orderData.clientEmail}
                    onChange={handleInputChange}
                    required={true}
                    style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="clientPhone"
                    value={orderData.clientPhone}
                    onChange={handleInputChange}
                    required={true}
                    style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                  />
                </div>
                
                <div>
                  <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                    Your Address *
                  </label>
                  <input
                    type="text"
                    name="clientAddress"
                    value={orderData.clientAddress}
                    onChange={handleInputChange}
                    required={true}
                    placeholder="Your business/mailing address"
                    style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                  />
                </div>
              </div>
            </div>

            {/* Case Information */}
            <div style={{ 
              backgroundColor: 'var(--bg-card)', 
              padding: '2rem', 
              borderRadius: '12px', 
              border: '1px solid var(--border-medium)',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ fontSize: '1.3rem', marginBottom: '1.5rem' }}>
                Case Information
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                    Case Number
                  </label>
                  <input
                    type="text"
                    name="caseNumberCommon"
                    value={orderData.caseNumberCommon}
                    onChange={handleInputChange}
                    placeholder="Case number if existing case"
                    style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                  />
                </div>
                
                <div>
                  <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                    Court and Branch *
                  </label>
                  <input
                    type="text"
                    name="courtAndBranch"
                    value={orderData.courtAndBranch}
                    onChange={handleInputChange}
                    required={true}
                    placeholder="e.g., Superior Court of California, LA County - Central Branch"
                    style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                    Plaintiff's Name *
                  </label>
                  <input
                    type="text"
                    name="plaintiffName"
                    value={orderData.plaintiffName}
                    onChange={handleInputChange}
                    required={true}
                    placeholder="Full name of plaintiff"
                    style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                  />
                </div>
                
                <div>
                  <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                    Defendant's Name *
                  </label>
                  <input
                    type="text"
                    name="defendantName"
                    value={orderData.defendantName}
                    onChange={handleInputChange}
                    required={true}
                    placeholder="Full name of defendant"
                    style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                    Plaintiff's Address *
                  </label>
                  <textarea
                    name="plaintiffAddress"
                    value={orderData.plaintiffAddress}
                    onChange={handleInputChange}
                    required={true}
                    rows="3"
                    placeholder="Complete address of plaintiff"
                    style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem', resize: 'vertical' }}
                  />
                </div>
                
                <div>
                  <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                    Defendant's Address *
                  </label>
                  <textarea
                    name="defendantAddress"
                    value={orderData.defendantAddress}
                    onChange={handleInputChange}
                    required={true}
                    rows="3"
                    placeholder="Complete address of defendant"
                    style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem', resize: 'vertical' }}
                  />
                </div>
              </div>
            </div>

            {/* Service-Specific Fields */}
            {orderData.orderType && (
              <div style={{ 
                backgroundColor: 'var(--bg-card)', 
                padding: '2rem', 
                borderRadius: '12px', 
                border: '1px solid var(--border-medium)',
                marginBottom: '2rem'
              }}>
                {renderOrderTypeFields()}
                
                {/* Special Instructions */}
                <div style={{ marginTop: '1.5rem' }}>
                  <label className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                    Special Instructions / Additional Information
                  </label>
                  <textarea
                    name="specialInstructions"
                    value={orderData.specialInstructions}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Any special instructions, deadlines, or additional information we should know..."
                    style={{ width: '100%', padding: '0.75rem 1rem', backgroundColor: 'var(--bg-page)', border: '1px solid var(--border-medium)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '1rem', resize: 'vertical' }}
                  />
                </div>
              </div>
            )}

            {/* File Upload */}
            <div style={{ 
              backgroundColor: 'var(--bg-card)', 
              padding: '2rem', 
              borderRadius: '12px', 
              border: '1px solid var(--border-medium)',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ fontSize: '1.3rem', marginBottom: '1rem' }}>
                Document Upload
              </h3>
              
              <div style={{
                border: '2px dashed var(--border-medium)',
                borderRadius: '8px',
                padding: '2rem',
                textAlign: 'center',
                marginBottom: '1rem'
              }}>
                <Upload size={32} color="var(--text-secondary)" style={{ marginBottom: '1rem' }} />
                <p className="body-medium" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                  Upload Documents
                </p>
                <p className="body-small" style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                  Drag and drop files or click to browse
                </p>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="btn-secondary">
                  Choose Files
                </label>
              </div>

              {files.length > 0 && (
                <div>
                  <p className="body-small" style={{ color: 'var(--text-primary)', marginBottom: '0.5rem', fontWeight: '600' }}>
                    Uploaded Files:
                  </p>
                  {files.map((file, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      padding: '0.5rem',
                      backgroundColor: 'var(--bg-page)',
                      borderRadius: '4px',
                      marginBottom: '0.5rem'
                    }}>
                      <span className="body-small" style={{ color: 'var(--text-primary)' }}>
                        {file.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--error-red)',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div style={{ textAlign: 'center' }}>
              {error && (
                <div style={{
                  backgroundColor: 'rgba(229, 62, 62, 0.1)',
                  border: '1px solid var(--error-red)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  color: 'var(--error-red)'
                }}>
                  {error}
                </div>
              )}
              
              {!orderData.orderType && (
                <div style={{
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid #3b82f6',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  color: '#1e40af'
                }}>
                  📋 Please select a service type above to enable the submit button
                </div>
              )}
              
              <button 
                type="submit" 
                className="btn-primary"
                disabled={!orderData.orderType || isSubmitting}
                style={{ 
                  fontSize: '1.1rem', 
                  padding: '1.2em 3em',
                  opacity: (!orderData.orderType || isSubmitting) ? 0.6 : 1,
                  cursor: (!orderData.orderType || isSubmitting) ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmitting ? 'Submitting Order...' : 'Submit Order Request'}
              </button>
              
              <p className="body-small" style={{ color: 'var(--text-secondary)', marginTop: '1rem' }}>
                By submitting this order, you agree to our terms of service. We'll contact you within 24 hours.
              </p>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PlaceOrderPage;